import React, {useEffect, useState, useMemo, useCallback} from 'react';
import {Link as RouterLink} from 'react-router-dom';
import {useMaterialReactTable, MaterialReactTable} from 'material-react-table';
import {
    Box, Typography, Paper, Grid, Stack, Chip, Divider,
    CircularProgress, Alert, Button, Link
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import apiService from '../../services/apiService';
import {getWinningColor, formatPlayerName} from '../../utils/formatters';
import {ROLES} from '../../config/roles';

const StatCard = ({label, value, color}) => (
    <Paper elevation={2} sx={{p: 2, textAlign: 'center'}}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="h4" color={color}>{value}</Typography>
    </Paper>
);

const LeaderboardTable = ({data}) => {
    const columns = useMemo(() => [
        {
            accessorKey: 'playerUid',
            header: 'Player',
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/players/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 16)}...
                </Link>
            ),
        },
        {accessorKey: 'totalGames', header: 'Games', size: 80},
        {accessorKey: 'totalWins', header: 'Wins', size: 80},
        {
            accessorKey: 'winRate',
            header: 'Win Rate',
            size: 90,
            Cell: ({cell}) => `${cell.getValue().toFixed(1)}%`,
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data,
        enablePagination: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableSorting: false,
    });

    return <MaterialReactTable table={table}/>;
};

const MachineStatsTable = ({data}) => {
    const columns = useMemo(() => [
        {
            accessorKey: 'machineId',
            header: 'Machine',
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/machines/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 12)}...
                </Link>
            ),
        },
        {accessorKey: 'totalGames', header: 'Games', size: 80},
        {accessorKey: 'totalWins', header: 'Wins', size: 80},
        {
            accessorKey: 'winRate',
            header: 'Win Rate',
            size: 90,
            Cell: ({cell}) => `${cell.getValue().toFixed(1)}%`,
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data,
        enablePagination: false,
        enableBottomToolbar: false,
        enableTopToolbar: false,
        enableSorting: false,
    });

    return <MaterialReactTable table={table}/>;
};

const RecentWinsTable = () => {
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isError, setIsError] = useState(false);
    const [rowCount, setRowCount] = useState(0);
    const [pagination, setPagination] = useState({pageIndex: 0, pageSize: 10});

    const fetchData = useCallback(() => {
        setIsLoading(true);
        const params = new URLSearchParams({page: pagination.pageIndex, size: pagination.pageSize});
        apiService.get(`/api/games/stats/recent-wins?${params}`)
            .then(page => {
                setData(page.content);
                setRowCount(page.totalElements);
                setIsError(false);
            })
            .catch(() => setIsError(true))
            .finally(() => setIsLoading(false));
    }, [pagination]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const columns = useMemo(() => [
        {
            accessorKey: 'finishedAt',
            header: 'Time',
            size: 170,
            Cell: ({cell}) => cell.getValue() ? new Date(cell.getValue()).toLocaleString() : '—',
        },
        {
            accessorKey: 'id',
            header: 'Game',
            size: 120,
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/games/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 8)}...
                </Link>
            ),
        },
        {
            id: 'player',
            header: 'Player',
            accessorFn: row => row.player?.name || row.player?.id,
            Cell: ({row}) => {
                const {player} = row.original;
                if (!player?.id) return '—';
                return (
                    <Link component={RouterLink} to={`/players/${player.id}`}>
                        {formatPlayerName(player)}
                    </Link>
                );
            },
        },
        {
            accessorKey: 'machineId',
            header: 'Machine',
            size: 120,
            Cell: ({cell}) => (
                <Link component={RouterLink} to={`/machines/${cell.getValue()}`}>
                    {cell.getValue()?.substring(0, 8)}...
                </Link>
            ),
        },
        {
            accessorKey: 'winning',
            header: 'Result',
            size: 80,
            Cell: ({cell}) => <Chip label="WIN" color="success" size="small"/>,
        },
    ], []);

    const table = useMaterialReactTable({
        columns,
        data,
        manualPagination: true,
        rowCount,
        state: {pagination, isLoading, showAlertBanner: isError},
        onPaginationChange: setPagination,
        muiToolbarAlertBannerProps: isError ? {color: 'error', children: 'Error loading wins'} : undefined,
        enableSorting: false,
    });

    return <MaterialReactTable table={table}/>;
};

const GameStatsPage = () => {
    const [overview, setOverview] = useState(null);
    const [leaderboard, setLeaderboard] = useState([]);
    const [machineStats, setMachineStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        Promise.all([
            apiService.get('/api/games/stats'),
            apiService.get('/api/games/stats/leaderboard'),
            apiService.get('/api/games/stats/machines'),
        ])
            .then(([ov, lb, ms]) => {
                setOverview(ov);
                setLeaderboard(lb);
                setMachineStats(ms);
            })
            .catch(err => setError(err.message || 'Failed to load statistics'))
            .finally(() => setLoading(false));
    }, []);

    if (loading) return <Box sx={{display: 'flex', justifyContent: 'center', p: 4}}><CircularProgress/></Box>;
    if (error) return <Alert severity="error" sx={{m: 2}}>{error}</Alert>;

    return (
        <Box sx={{p: 2}}>
            <Typography variant="h4" gutterBottom>Game Statistics</Typography>

            {overview && (
                <Grid container spacing={2} sx={{mb: 3}}>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Total Games" value={overview.totalGames.toLocaleString()}/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Total Wins" value={overview.totalWins.toLocaleString()} color="success.main"/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Win Rate" value={`${overview.winRate.toFixed(1)}%`}/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Unique Players" value={overview.uniquePlayers.toLocaleString()}/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="In Progress" value={overview.inProgress.toLocaleString()} color="primary.main"/>
                    </Grid>
                    <Grid size={{xs: 6, sm: 4, md: 2}}>
                        <StatCard label="Errors" value={overview.totalErrors.toLocaleString()} color="error.main"/>
                    </Grid>
                </Grid>
            )}

            <Grid container spacing={3} sx={{mb: 3}}>
                <Grid size={{xs: 12, md: 6}}>
                    <Paper elevation={2} sx={{p: 2}}>
                        <Typography variant="h6" gutterBottom>Top 10 Players by Wins</Typography>
                        <Divider sx={{mb: 1}}/>
                        {leaderboard.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{py: 2}}>No data yet.</Typography>
                        ) : (
                            <LeaderboardTable data={leaderboard}/>
                        )}
                    </Paper>
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <Paper elevation={2} sx={{p: 2}}>
                        <Typography variant="h6" gutterBottom>Stats by Machine</Typography>
                        <Divider sx={{mb: 1}}/>
                        {machineStats.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{py: 2}}>No data yet.</Typography>
                        ) : (
                            <MachineStatsTable data={machineStats}/>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            <Box sx={{mb: 3}}>
                <Typography variant="h5" gutterBottom>Recent Wins</Typography>
                <RecentWinsTable/>
            </Box>

            <Button component={RouterLink} to="/games" variant="outlined" startIcon={<ArrowBackIcon/>}>
                Back to Games
            </Button>
        </Box>
    );
};

GameStatsPage.abilities = {
    read: [ROLES.VIEWERS, ROLES.SUPPORT, ROLES.ADMINS],
};

export default GameStatsPage;
